import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "../core/auth/auth-service.service";
import { environment } from "src/environments/environment";
import { capitalize } from "../utils/functions";
import { tap } from "rxjs";
import { User } from "../models/auth/user.model";
import { PortalUser } from "../models/user/user.model";
import { TouchConfig } from "../models/touch/touch.model";

@Injectable({
	providedIn: 'root',
})
export class AdminService {
	private apiUrl: String = environment.apiUrl;
	private domainApiUrl: String = environment.apiDomain;
	private apiKey: String = environment.apiPlenionKey;
	private apiPlenion: String = environment.apiPlenion;
    
    token: string = "";

    constructor(private http: HttpClient, private translate: TranslateService, private authService: AuthService) {
		this.token = this.authService.getToken();
	}

	private getAuthHeaders() {
		return {
			headers: {
				Authorization: `Bearer ${this.authService.getToken()}`
			}
		};
	}

    getDomainUsers() {
        const url =  `${this.apiPlenion}/admin/users?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	getDomainUser(userId: string) {
		const url =  `${this.apiPlenion}/admin/users/user?&key=` + this.apiKey + '&auth=' + this.authService.getToken()
			+ '&id=' + userId;

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	getTouchUsers() {
		const url =  `${this.apiPlenion}/admin/touch?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<any>(url, this.getAuthHeaders());
	}

	changeTouchAccess(contactId: string, contactEmail: string, customerId: string) {
		let body = {
			"email": contactEmail, 
			"customer": customerId
		}

		const url =  `${this.apiPlenion}/admin/touch/` + contactId + `?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));
	}

	setDomainUserAccess(userId: string, email: string, api: boolean, portal: boolean, admin: boolean, useDefaultTypes: boolean, useDefaultResources: boolean) {
		let body = {
			"email": email,
			"api": api,
			"portal": portal,
			"admin": admin,
			"useDefaultTypes": useDefaultTypes,
			"useDefaultResources": useDefaultResources
		}

		const url =  `${this.apiPlenion}/admin/users/` + userId + `?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));
	}

	setUserTypes(userId: string, newTypes: string[]) {
		let body = { "types": newTypes }

		const url =  `${this.apiPlenion}/admin/users/` + userId + `/usertypes?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));
	}

	setUserResources(userId: string, newResources: string[]) {
		let body = { "resources": newResources }

		const url =  `${this.apiPlenion}/admin/users/` + userId + `/userresources?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));
	}

	addUser(userId: string, userEmail: string, userPass: string, apiEnabled: boolean, portalEnabled: boolean, isAdmin: boolean){
		let body = {
			"ID": userId,
			"email": userEmail,
			"passwd": userPass,
			"api": apiEnabled,
			"portal": portalEnabled,
			"admin": isAdmin
		}
		
		const url =  `${this.apiPlenion}/admin/users/user?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.post<any>(url,body,this.getAuthHeaders());
	}

	deleteUser(userId: string, userEmail: string) {
		let body = { 
			"idUser": userId,
			"email": userEmail
		}

		const url =  `${this.apiPlenion}/admin/users/user?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.delete<any>(url, {...this.getAuthHeaders(), body: body}).pipe(tap((response) => {}));
	}

	deleteUsers(usersMap: any[]) {
		let myUsers: any[] = [];

		usersMap.forEach((user) => {
			myUsers.push({
				"idUser": user.UserID,
				"email": user.Email
			});
		});
		let body = { users: myUsers };

		const url = `${this.apiPlenion}/admin/users/user?&key=` + this.apiKey + '&auth=' + this.authService.getToken();
	
		return this.http.delete<any>(url, {...this.getAuthHeaders(), body: body }).pipe(tap((response) => {}));
	}

	getDomainTypes() {
		const url =  `${this.apiPlenion}/admin/types?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());	
	}

	getDomainResources() {
		const url =  `${this.apiPlenion}/admin/resources?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());	
	}

	updateDomainTypes(newResources: string[]) {
		let body = { "types": newResources }

		const url =  `${this.apiPlenion}/admin/types?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));	
	}

	updateDomainResources(newTypes: string[]) {
		let body = { "resources": newTypes }

		const url =  `${this.apiPlenion}/admin/resources?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));	
	}

	getInvoicesInfo() {
        const url =  `${this.apiPlenion}/admin/invoices?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	getMailsInfo() {
        const url =  `${this.apiPlenion}/admin/mails?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	addAllowedEmail(email: string, isInvoices: boolean) {
		let body = { "email": email }
		let endpoint = isInvoices ? "invoices" : "mails";

		const url =  `${this.apiPlenion}/admin/` + endpoint + `?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));	
	}

	removeAllowedEmail(email: string, isInvoices: boolean) {
		let body = { 
			"email": email
		}
		let endpoint = isInvoices ? "invoices" : "mails";

		const url =  `${this.apiPlenion}/admin/` + endpoint + `?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.delete<any>(url, {...this.getAuthHeaders(), body: body}).pipe(tap((response) => {}));
	}

	getTouchConfiguration() {
        const url =  `${this.apiPlenion}/admin/touch/config?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	updateTouchConfiguration(touchConfig: TouchConfig, myLogo: string) {
		let body = { 
			"welcomeMsg": touchConfig.welcomeMsg,
			"email": touchConfig.email,
			"fiscalInfo": touchConfig.fiscalInfo,
			"address": touchConfig.address,
			"phoneNumber": touchConfig.phoneNumber,
			"websiteURL": touchConfig.websiteURL,
			"logo": myLogo
		}

		const url =  `${this.apiPlenion}/admin/touch/config?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));	
	}

	getDocumentsLabels() {
        const url =  `${this.apiPlenion}/admin/documentslabels?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	addDocumentLabel(en: string, fr: string, nl: string) {
		let body = {
			"en": en,
			"fr": fr,
			"nl": nl
		}
		
		const url =  `${this.apiPlenion}/admin/documentslabels?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.post<any>(url,body, this.getAuthHeaders());
	}

	initializeDocumentsLabels() {
		const url =  `${this.apiPlenion}/admin/documentslabels/initialize?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.post<any>(url, this.getAuthHeaders());	
	}

	getMissingFolders(type: String) {
		const url =  `${this.apiPlenion}/admin/folders?&key=` + this.apiKey + '&auth=' + this.authService.getToken() + '&type=' + type;

		return this.http.get<JSON>(url, this.getAuthHeaders());	
	}
	
	syncMissingFolders(type: String, folders: string[]) {
		let body = { "folders": folders }
		
		const url =  `${this.apiPlenion}/admin/folders?&key=` + this.apiKey + '&auth=' + this.authService.getToken() + '&type=' + type;

		return this.http.post<any>(url, body, this.getAuthHeaders());	
	}

	recreateMainFolders() {
		const url =  `${this.apiPlenion}/admin/folders/recreate?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());	
	}

	getAllDomainsV2() {
		const url =  `${this.domainApiUrl}/admin/support/getAllDomains?&key=` + this.apiKey;

		return this.http.get<JSON>(url, this.getAuthHeaders());	
	}

	getAllDomains() {
		const url =  `${this.apiPlenion}/admin/support/getAllDomains?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());	
	}

	redirectToDomain(domainId: string) {
		const url =  `${this.domainApiUrl}/admin/support?domain=` + domainId;

		return this.http.post<JSON>(url, this.getAuthHeaders());	
	}

	checkInvoicePermission() {
		const url =  `${this.apiPlenion}/admin/support/invoice?key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	addInvoiceCredits(credits: number) {
		const url =  `${this.apiPlenion}/admin/support/invoice?key=` + this.apiKey + '&auth=' + this.authService.getToken() + '&credits=' + credits.toString();

		return this.http.post<JSON>(url, this.getAuthHeaders());
	}

	changeLabelAccess(label: any) {
		let body = {
			"labelID": label.id, 
		}

		const url =  `${this.apiPlenion}/admin/documentslabels/status?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.put<any>(url, body, this.getAuthHeaders()).pipe(tap((response) => {}));
	}

	getRoutesInfo() {
        const url =  `${this.apiPlenion}/admin/routes?&key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

	addRoutesCredits(credits: number) {
		const url =  `${this.apiPlenion}/admin/routes?key=` + this.apiKey + '&auth=' + this.authService.getToken() + '&credits=' + credits.toString();

		return this.http.post<JSON>(url, this.getAuthHeaders());
	}

	getAddUsersList() {
		const url =  `${this.apiPlenion}/admin/users/addList?key=` + this.apiKey + '&auth=' + this.authService.getToken();

		return this.http.get<JSON>(url, this.getAuthHeaders());
	}

}